import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import api from '../api/client';
import toast from 'react-hot-toast';
import PhoneInput from '../components/PhoneInput';
import {
  User, ChevronRight, ChevronLeft, CheckCircle, Stethoscope,
  Shield, Loader2, Search, Building2, MapPin, UserPlus, UserCheck,
  Clock, ChevronDown, ChevronUp
} from 'lucide-react';
import LocationAutocomplete from '../components/LocationAutocomplete';

const STEPS = [
  { id: 1, label: 'Quick Setup',        icon: User },
  { id: 2, label: 'Emergency Contact',  icon: Shield },
  { id: 3, label: 'Find a Doctor',      icon: Stethoscope },
];

const LANGUAGES = ['English', 'French', 'Spanish', 'Arabic', 'Mandarin', 'Portuguese', 'Hindi', 'Other'];
const GENDERS = [
  { value: 'male',              label: 'Male' },
  { value: 'female',            label: 'Female' },
  { value: 'non-binary',        label: 'Non-binary' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

const PatientOnboarding = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep]     = useState(1);
  const [saving, setSaving] = useState(false);
  const [skipping, setSkipping] = useState(false);

  // Step 1 — Quick Setup
  const [phone, setPhone]           = useState('');
  const [showOptional, setShowOptional] = useState(false);
  const [dob, setDob]               = useState('');
  const [gender, setGender]         = useState('');
  const [language, setLanguage]     = useState('English');

  // Step 2 — Emergency Contact
  const [ecName, setEcName]         = useState('');
  const [ecPhone, setEcPhone]       = useState('');
  const [ecRelation, setEcRelation] = useState('');
  const [livesAlone, setLivesAlone] = useState(false);
  const [hasCaregiver, setHasCaregiver] = useState(false);

  // Step 3 — Doctor Assignment
  const [hasProvider, setHasProvider] = useState(null);
  const [providerSearch, setProviderSearch] = useState('');
  const [providerLocation, setProviderLocation] = useState({ city: '', state: '', zipCode: '' });
  const [doctorSearchResults, setDoctorSearchResults] = useState([]);
  const [existingRequests, setExistingRequests] = useState([]);
  const [patientLocation, setPatientLocation] = useState({ city: '', state: '', zipCode: '' });
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentError, setAssignmentError] = useState(null);
  const [assignmentSuccess, setAssignmentSuccess] = useState(false);

  const totalSteps = STEPS.length;
  const progress = (step / totalSteps) * 100;

  const saveAndExit = async () => {
    setSkipping(true);
    try {
      const updates = { onboardingCompleted: true };
      if (phone) await api.put('/profile', { phone }).catch(() => {});
      await api.put('/patient-profile/me', updates);
    } catch { /* non-fatal */ }
    sessionStorage.setItem('profileBannerDismissed', 'true');
    navigate('/dashboard');
  };

  const handleNext = () => setStep(s => Math.min(s + 1, totalSteps));
  const handleBack = () => setStep(s => Math.max(s - 1, 1));

  // Step 3 — Doctor Assignment Functions
  const searchExistingProvider = async () => {
    if (!providerSearch.trim()) return;
    setAssignmentLoading(true);
    try {
      const [searchResponse, requestsResponse] = await Promise.all([
        api.get('/doctor-assignment/search', {
          params: { name: providerSearch, location: providerLocation.city, limit: 10 }
        }),
        api.get('/care-requests/my-requests')
      ]);
      setDoctorSearchResults(searchResponse.data?.data || []);
      setExistingRequests(requestsResponse.data?.data || []);
    } catch (err) {
      setAssignmentError('Failed to search for doctors');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const hasPendingRequest = (doctorId) =>
    existingRequests.some(req => req.doctorId?._id === doctorId && req.status === 'pending');

  const requestExistingProvider = async (doctorId) => {
    setAssignmentLoading(true);
    try {
      await api.post('/doctor-assignment/request-doctor', {
        doctorId,
        message: 'Patient is transferring care to your practice'
      });
      toast.success('Care request sent successfully!');
      navigate('/care-requests');
    } catch (err) {
      setAssignmentError('Failed to send care request');
      toast.error('Failed to send care request');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleAutoAssign = async () => {
    setAssignmentLoading(true);
    setAssignmentError(null);
    try {
      const response = await api.post('/doctor-assignment/auto-assign', {
        location: patientLocation.city,
        urgency: 'normal'
      });
      if (response.data?.data?.success) {
        setAssignmentSuccess(true);
      } else {
        setAssignmentError(response.data?.data?.message || 'No doctors available');
      }
    } catch (err) {
      setAssignmentError('Failed to auto-assign doctor');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const searchAvailableDoctors = async () => {
    setAssignmentLoading(true);
    try {
      const response = await api.get('/doctor-assignment/search', {
        params: { location: patientLocation.city, limit: 20 }
      });
      setDoctorSearchResults(response.data?.data || []);
    } catch (err) {
      setAssignmentError('Failed to load available doctors');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      const demographicsPayload = {};
      if (dob)      demographicsPayload.dateOfBirth      = dob;
      if (gender)   demographicsPayload.gender            = gender;
      if (language) demographicsPayload.preferredLanguage = language;
      demographicsPayload.livesAlone   = livesAlone;
      demographicsPayload.hasCaregiver = hasCaregiver;
      if (ecName || ecPhone) {
        demographicsPayload.caregiverContact = {
          name:         ecName     || undefined,
          phone:        ecPhone    || undefined,
          relationship: ecRelation || undefined,
        };
      }

      const updates = { onboardingCompleted: true };
      if (Object.keys(demographicsPayload).length) updates.demographics = demographicsPayload;

      if (phone) await api.put('/profile', { phone }).catch(() => {});

      await api.put('/patient-profile/me', updates);
      sessionStorage.setItem('profileBannerDismissed', 'true');
      toast.success('Profile set up! Welcome to SymptoConnect.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none';
  const labelCls = 'block text-xs font-extrabold text-slate-500 uppercase tracking-[0.15em] mb-2';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-primary/[0.03] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary/20">
            <Stethoscope className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-headline text-2xl font-extrabold text-slate-900 tracking-tight">
            Let's set up your profile
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Under 2 minutes · Skip anything you're not ready to share
          </p>
        </div>

        {/* Step indicators */}
        <div className="flex items-center gap-2 mb-6">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done    = step > s.id;
            const current = step === s.id;
            return (
              <React.Fragment key={s.id}>
                <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold transition-all ${
                  done    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                  current ? 'bg-primary/10 text-primary border border-primary/20' :
                            'bg-slate-100 text-slate-400 border border-transparent'
                }`}>
                  {done
                    ? <CheckCircle className="w-3.5 h-3.5" />
                    : <Icon className="w-3.5 h-3.5" />
                  }
                  <span className="hidden sm:block">{s.label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-px transition-all ${step > s.id ? 'bg-emerald-300' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-slate-200 rounded-full mb-8 overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-100">

          {/* ── STEP 1: Quick Setup ── */}
          {step === 1 && (
            <div className="p-7 space-y-5">
              <div>
                <p className="text-xs font-extrabold text-primary uppercase tracking-widest mb-1">Step 1 of {totalSteps}</p>
                <h2 className="font-headline text-xl font-extrabold text-slate-900">Your phone number</h2>
                <p className="text-sm text-slate-500 mt-0.5">So your care team can reach you when it matters.</p>
              </div>

              <div>
                <label className={labelCls}>Phone Number</label>
                <PhoneInput value={phone} onChange={setPhone} />
              </div>

              {/* Quick exit — saves phone and goes to dashboard */}
              <button
                onClick={saveAndExit}
                disabled={skipping}
                className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white rounded-xl text-sm font-extrabold hover:bg-primary/90 transition-all shadow-sm shadow-primary/20 active:scale-[0.98] disabled:opacity-50"
              >
                {skipping
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
                  : <><CheckCircle className="w-4 h-4" /> Go to dashboard</>
                }
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-100" />
                </div>
                <div className="relative flex justify-center text-xs text-slate-400 font-medium">
                  <span className="bg-white px-3">or continue to add more details</span>
                </div>
              </div>

              {/* Optional details toggle */}
              <button
                onClick={() => setShowOptional(v => !v)}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all"
              >
                <span>Optional details (date of birth, language…)</span>
                {showOptional ? <ChevronUp className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
              </button>

              {showOptional && (
                <div className="grid grid-cols-2 gap-4 pt-1">
                  <div className="col-span-2 sm:col-span-1">
                    <label className={labelCls}>Date of Birth</label>
                    <input type="date" value={dob} onChange={e => setDob(e.target.value)}
                      max={new Date().toISOString().slice(0,10)}
                      className={inputCls} />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className={labelCls}>Gender</label>
                    <select value={gender} onChange={e => setGender(e.target.value)} className={inputCls}>
                      <option value="">Select gender</option>
                      {GENDERS.map(g => (
                        <option key={g.value} value={g.value}>{g.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className={labelCls}>Preferred Language</label>
                    <select value={language} onChange={e => setLanguage(e.target.value)} className={inputCls}>
                      {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 2: Emergency Contact ── */}
          {step === 2 && (
            <div className="p-7 space-y-5">
              <div>
                <p className="text-xs font-extrabold text-primary uppercase tracking-widest mb-1">Step 2 of {totalSteps}</p>
                <h2 className="font-headline text-xl font-extrabold text-slate-900">Emergency Contact</h2>
                <p className="text-sm text-slate-500 mt-0.5">Used only in case of a medical emergency. Optional.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <label className={labelCls}>Contact Name</label>
                  <input type="text" value={ecName} onChange={e => setEcName(e.target.value)}
                    placeholder="Full name" className={inputCls} />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label className={labelCls}>Relationship</label>
                  <input type="text" value={ecRelation} onChange={e => setEcRelation(e.target.value)}
                    placeholder="e.g. Spouse, Parent" className={inputCls} />
                </div>
                <div className="col-span-2">
                  <label className={labelCls}>Contact Phone</label>
                  <PhoneInput value={ecPhone} onChange={setEcPhone} />
                </div>
              </div>

              <div className="space-y-3 pt-1">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div
                    onClick={() => setLivesAlone(v => !v)}
                    className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                      livesAlone ? 'bg-primary border-primary' : 'border-slate-300 group-hover:border-primary/50'
                    }`}
                  >
                    {livesAlone && <CheckCircle className="w-3 h-3 text-white" />}
                  </div>
                  <span className="text-sm font-medium text-slate-700">I live alone</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div
                    onClick={() => setHasCaregiver(v => !v)}
                    className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                      hasCaregiver ? 'bg-primary border-primary' : 'border-slate-300 group-hover:border-primary/50'
                    }`}
                  >
                    {hasCaregiver && <CheckCircle className="w-3 h-3 text-white" />}
                  </div>
                  <span className="text-sm font-medium text-slate-700">I have a regular caregiver</span>
                </label>
              </div>
            </div>
          )}

          {/* ── STEP 3: Find a Doctor ── */}
          {step === 3 && (
            <div className="p-7 space-y-5">
              <div>
                <p className="text-xs font-extrabold text-primary uppercase tracking-widest mb-1">Step 3 of {totalSteps}</p>
                <h2 className="font-headline text-xl font-extrabold text-slate-900">Connect with a Doctor</h2>
                <p className="text-sm text-slate-500 mt-0.5">To provide continuous care, we need to connect you with a healthcare provider.</p>
              </div>

              {assignmentSuccess ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Request Sent!</h3>
                  <p className="text-slate-600 mb-4">Your care request has been sent. The doctor will contact you within 24 hours.</p>
                </div>
              ) : hasProvider === null ? (
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800">Do you currently have a primary care provider?</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => setHasProvider(true)}
                      className="w-full p-4 border-2 border-slate-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <UserCheck className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">Yes, I have a doctor</p>
                          <p className="text-sm text-slate-500">Search for your existing provider</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 ml-auto" />
                      </div>
                    </button>
                    <button
                      onClick={() => setHasProvider(false)}
                      className="w-full p-4 border-2 border-slate-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <UserPlus className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">No, I need a doctor</p>
                          <p className="text-sm text-slate-500">We'll help you find one</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 ml-auto" />
                      </div>
                    </button>
                  </div>
                </div>
              ) : hasProvider ? (
                <div className="space-y-4">
                  <div>
                    <label className={labelCls}>Doctor's Name or Practice</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        value={providerSearch}
                        onChange={(e) => setProviderSearch(e.target.value)}
                        placeholder="Dr. Smith or City Medical Group"
                        className={inputCls + " pl-10"}
                        onKeyPress={(e) => e.key === 'Enter' && searchExistingProvider()}
                      />
                    </div>
                  </div>
                  <LocationAutocomplete
                    label="Doctor's Location (optional)"
                    value={providerLocation}
                    onChange={setProviderLocation}
                    placeholder="City or ZIP code"
                  />
                  <button
                    onClick={searchExistingProvider}
                    disabled={!providerSearch.trim() || assignmentLoading}
                    className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {assignmentLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                    Search
                  </button>
                  {assignmentError && <p className="text-sm text-red-600">{assignmentError}</p>}
                  {doctorSearchResults.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-700">{doctorSearchResults.length} doctors found</p>
                      {doctorSearchResults.map((doctor) => (
                        <button
                          key={doctor.id}
                          onClick={() => requestExistingProvider(doctor.id)}
                          className="w-full p-3 border border-slate-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                              <Stethoscope className="w-5 h-5 text-slate-500" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">Dr. {doctor.name}</p>
                              <p className="text-sm text-slate-500">{doctor.specialization}</p>
                              {doctor.institution && (
                                <p className="text-xs text-slate-400 flex items-center gap-1">
                                  <Building2 className="w-3 h-3" /> {doctor.institution}
                                </p>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <LocationAutocomplete
                    label="Your Location"
                    value={patientLocation}
                    onChange={setPatientLocation}
                    placeholder="Enter your city or address"
                  />
                  <div className="space-y-3">
                    <button
                      onClick={handleAutoAssign}
                      disabled={!patientLocation.city || assignmentLoading}
                      className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {assignmentLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <UserPlus className="w-5 h-5" />}
                      Find Me a Doctor (Auto)
                    </button>
                    <button
                      onClick={searchAvailableDoctors}
                      disabled={!patientLocation.city || assignmentLoading}
                      className="w-full py-3 border-2 border-blue-600 text-blue-600 rounded-lg font-medium hover:bg-blue-50 disabled:border-slate-300 disabled:text-slate-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <Search className="w-5 h-5" />
                      Browse Available Doctors
                    </button>
                  </div>
                  {assignmentError && <p className="text-sm text-red-600">{assignmentError}</p>}
                  {doctorSearchResults.length > 0 && (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      <p className="text-sm font-medium text-slate-700">{doctorSearchResults.length} doctors available</p>
                      {doctorSearchResults.map((doctor) => {
                        const isPending = hasPendingRequest(doctor.id);
                        return (
                          <button
                            key={doctor.id}
                            onClick={() => !isPending && requestExistingProvider(doctor.id)}
                            disabled={isPending}
                            className={`w-full p-3 border rounded-lg transition-all text-left ${
                              isPending
                                ? 'border-amber-200 bg-amber-50 cursor-not-allowed'
                                : 'border-slate-200 hover:border-blue-500 hover:bg-blue-50'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                isPending ? 'bg-amber-100' : 'bg-slate-100'
                              }`}>
                                {isPending
                                  ? <Clock className="w-5 h-5 text-amber-600" />
                                  : <Stethoscope className="w-5 h-5 text-slate-500" />
                                }
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-slate-900">Dr. {doctor.name}</p>
                                  {isPending && (
                                    <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                                      Request Pending
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-slate-500">{doctor.specialization}</p>
                                {doctor.institution && (
                                  <p className="text-xs text-slate-400 flex items-center gap-1">
                                    <Building2 className="w-3 h-3" /> {doctor.institution}
                                  </p>
                                )}
                                {doctor.location?.city && (
                                  <p className="text-xs text-slate-400 flex items-center gap-1">
                                    <MapPin className="w-3 h-3" /> {doctor.location.city}
                                  </p>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Footer nav */}
          <div className="px-7 pb-7 flex items-center justify-between gap-3">
            {step > 1 ? (
              <button onClick={handleBack} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50 border border-slate-200 transition-all">
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
            ) : (
              /* On step 1 the primary CTA is already inside the card; left slot is empty */
              <div />
            )}

            {step < totalSteps ? (
              <div className="flex items-center gap-3">
                {step > 1 && (
                  <button
                    onClick={saveAndExit}
                    disabled={skipping}
                    className="text-xs font-semibold text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    Skip this step
                  </button>
                )}
                <button onClick={handleNext} className="flex items-center gap-2 px-5 py-3 bg-slate-900 text-white rounded-xl text-sm font-extrabold hover:bg-primary transition-all shadow-sm active:scale-[0.98]">
                  Continue <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  onClick={saveAndExit}
                  disabled={skipping}
                  className="text-xs font-semibold text-slate-400 hover:text-slate-600 transition-colors"
                >
                  Skip this step
                </button>
                <button onClick={handleFinish} disabled={saving} className="flex items-center gap-2 px-5 py-3 bg-primary text-white rounded-xl text-sm font-extrabold hover:bg-primary/90 transition-all shadow-sm shadow-primary/20 active:scale-[0.98] disabled:opacity-50">
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><CheckCircle className="w-4 h-4" /> Finish Setup</>}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Privacy note */}
        <p className="text-center text-xs text-slate-400 mt-5 font-medium">
          <Shield className="w-3 h-3 inline mr-1" />
          Your health data is encrypted and never shared without your consent.
        </p>
      </div>
    </div>
  );
};

export default PatientOnboarding;
